<script setup lang="ts">
import { onMounted, onUnmounted, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useAppStore } from './stores/appStore'
import { useAuthStore } from './stores/authStore'
import { hasActiveModal } from './composables/useModalBackHandler'
import { handlePageNavigationKeyDown } from './composables/usePageNavigation'
import { navigateAppBack, initHistoryGuard, primeAppHistory } from './composables/useAppNavigation'
import { useSwipeDrawer } from './composables/useSwipeDrawer'
import { initGlobalNumberInputHandler } from './utils/numberInputHelper'
import AppLayout from './layouts/AppLayout.vue'

const store = useAppStore()
const authStore = useAuthStore()
const router = useRouter()
const { isSidebarOpen, closeSidebar } = useSwipeDrawer()

// Real-time automatic redirection when account is blocked or logged out on protected routes
watch(
  () => [authStore.isAccountBlocked, authStore.user],
  ([isBlocked, currentUser]) => {
    if (isBlocked) {
      if (router?.currentRoute?.value?.name !== 'login') {
        router?.push('/login').catch(() => {})
      }
    } else if (!currentUser && router?.currentRoute?.value?.meta?.requiresAuth) {
      router?.push('/login').catch(() => {})
    }
  }
)

// Global context menu prevention during touch release of a long press gesture
const onContextMenu = (e: MouseEvent) => {
  if ((window as any).isLongPressActive) {
    e.preventDefault()
    e.stopPropagation()
    ;(window as any).isLongPressActive = false
  }
}

// Handle Mouse Button 3 (Back) & Mouse Button 4 (Forward) history navigation (strictly 1 navigation per individual physical click)
let isBackMouseDown = false
let isForwardMouseDown = false

const triggerNavBack = () => {
  if (hasActiveModal()) {
    window.history.back()
    return
  }
  if (!navigateAppBack(router)) {
    router?.back?.()
  }
}

const triggerNavForward = () => {
  router?.forward?.()
}

const handleMouseHistoryButtons = (e: MouseEvent | PointerEvent) => {
  const isBack = e.button === 3 || (e as any).which === 4
  const isForward = e.button === 4 || (e as any).which === 5

  if (e.type === 'pointerdown' || e.type === 'mousedown') {
    if (isBack) {
      if (e.cancelable) e.preventDefault()
      e.stopPropagation()
      if (!isBackMouseDown) {
        isBackMouseDown = true
        triggerNavBack()
      }
    } else if (isForward) {
      if (e.cancelable) e.preventDefault()
      e.stopPropagation()
      if (!isForwardMouseDown) {
        isForwardMouseDown = true
        triggerNavForward()
      }
    }
  } else if (e.type === 'pointerup' || e.type === 'mouseup' || e.type === 'auxclick') {
    isBackMouseDown = false
    isForwardMouseDown = false
    if (isBack || isForward) {
      if (e.cancelable) e.preventDefault()
      e.stopPropagation()
    }
  }
}

const onWindowBlur = () => {
  isBackMouseDown = false
  isForwardMouseDown = false
}

const handleKeyHistoryButtons = (e: KeyboardEvent) => {
  if (handlePageNavigationKeyDown(e, router, authStore, store)) {
    return
  }

  if (e.repeat) {
    if (e.key === 'BrowserBack' || e.key === 'BrowserForward') {
      e.preventDefault()
    }
    return
  }

  if (e.key === 'BrowserBack' || (e.altKey && e.key === 'ArrowLeft')) {
    e.preventDefault()
    triggerNavBack()
  } else if (e.key === 'BrowserForward' || (e.altKey && e.key === 'ArrowRight')) {
    e.preventDefault()
    triggerNavForward()
  } else if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
    e.preventDefault()
    store.isGlobalSearchOpen = !store.isGlobalSearchOpen
  } else if (e.key === 'Escape') {
    if (!hasActiveModal()) {
      if (isSidebarOpen.value) {
        e.preventDefault()
        closeSidebar()
      }
    }
  }
}

const mouseHistoryEvents: (keyof WindowEventMap)[] = ['pointerdown', 'pointerup', 'mousedown', 'mouseup', 'auxclick']

const updateLastActiveTime = () => {
  try {
    localStorage.setItem('last_active_time', Date.now().toString())
  } catch {}
}

const onVisibilityChange = () => {
  if (document.visibilityState === 'hidden') {
    updateLastActiveTime()
  }
}

let cleanupNumberInputHandler: (() => void) | null = null

onMounted(async () => {
  cleanupNumberInputHandler = initGlobalNumberInputHandler()
  if (router) {
    initHistoryGuard(router)
    primeAppHistory(router)
  }
  await store.initialize()
  window.addEventListener('contextmenu', onContextMenu, { capture: true })
  window.addEventListener('keydown', handleKeyHistoryButtons, { capture: true })
  window.addEventListener('blur', onWindowBlur)
  document.addEventListener('visibilitychange', onVisibilityChange)
  window.addEventListener('pagehide', updateLastActiveTime)
  window.addEventListener('beforeunload', updateLastActiveTime)
  mouseHistoryEvents.forEach(evt => {
    window.addEventListener(evt, handleMouseHistoryButtons as any, { capture: true })
    document.addEventListener(evt, handleMouseHistoryButtons as any, { capture: true })
  })
})

onUnmounted(() => {
  cleanupNumberInputHandler?.()
  window.removeEventListener('contextmenu', onContextMenu, { capture: true })
  window.removeEventListener('keydown', handleKeyHistoryButtons, { capture: true })
  window.removeEventListener('blur', onWindowBlur)
  document.removeEventListener('visibilitychange', onVisibilityChange)
  window.removeEventListener('pagehide', updateLastActiveTime)
  window.removeEventListener('beforeunload', updateLastActiveTime)
  mouseHistoryEvents.forEach(evt => {
    window.removeEventListener(evt, handleMouseHistoryButtons as any, { capture: true })
    document.removeEventListener(evt, handleMouseHistoryButtons as any, { capture: true })
  })
})
</script>

<template>
  <AppLayout />
</template>

<style scoped>
</style>
